import { useRef, useState, useEffect } from 'react';
import {
    Text,
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputLeftAddon,
    Select,
    Image,
    useDisclosure,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import uploadCloud from 'assets/uploadCloud.png';
import { DashboardSection } from 'pages/dashboard/components/DashboardStyles';
import { create_store } from 'redux/actions/storeActions';
import { config } from 'config/config';
import StoreCreationSuccessModal from 'components/Store/StoreCreationSuccessModal';

export default function CreateStoreForm() {
    const {
        isOpen: isSuccessModalOpen,
        onClose: closeSuccessModal,
        onOpen: openSuccessModal,
    } = useDisclosure();
    const fileInput = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const storeReducer = useSelector((state) => state.storeReducer);
    const { store, storeCreated } = storeReducer;
    const [selectedImage, setSelectedImage] = useState('');
    const [storeDetail, setStoreDetail] = useState({});

    useEffect(() => {
        const social_handles = store?.social_handles ? JSON.parse(store.social_handles) : '';
        const settlement_account = store?.settlement_account
            ? JSON.parse(store.settlement_account)
            : '';
        setStoreDetail({
            name: store?.name,
            link: store?.link,
            social_handles: {
                facebook: social_handles?.facebook,
                instagram: social_handles?.instagram,
                twitter: social_handles?.twitter,
            },
            settlement_account: {
                account_name: settlement_account?.account_name,
                bank: settlement_account?.bank,
                account: settlement_account?.account,
            },
            logo_url: store?.logo_url,
        });
        if (storeCreated) {
            openSuccessModal();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store, storeCreated]);

    const handleChange = (e) => {
        if (['facebook', 'instagram', 'twitter'].includes(e.target.name)) {
            storeDetail.social_handles[e.target.name] = e.target.value;
            return setStoreDetail(storeDetail);
        }

        if (['account_name', 'bank', 'account'].includes(e.target.name)) {
            storeDetail.settlement_account[e.target.name] = e.target.value;
            return setStoreDetail(storeDetail);
        }

        storeDetail[e.target.name] = e.target.value;
        return setStoreDetail(storeDetail);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleSubmit = () => {
        dispatch(create_store(storeDetail, selectedImage));
    };

    const formArr = [
        {
            label: 'Store Name',
            placeholder: '3134 Designs',
            value: storeDetail.name,
            name: 'name',
        },
        {
            label: 'Store Link',
            placeholder: '3134 Designs',
            addon: `${config.baseUrl}/store/`,
            value: storeDetail.link,
            name: 'link',
        },
        {
            label: 'Social Handles',
            addon: ['Facebook', 'Twitter', 'Instagram'],
            placeholder: [
                'http://www.facebook.com',
                'http://www.twitter.com',
                'http://instagram.com',
            ],
            value: [
                storeDetail.social_handles?.facebook,
                storeDetail.social_handles?.twitter,
                storeDetail.social_handles?.instagram,
            ],
            name: ['facebook', 'twitter', 'instagram'],
        },
        {
            label: 'Upload Logo',
            placeholder: 'Click here to upload files or drag and drop',
            value: storeDetail.logo_url,
            name: 'logo_url',
        },
        {
            label: 'Settlement Account',
            placeholder: ['Select Bank', 'GTBank', 'Access Bank', 'First Bank'],
            value: storeDetail.settlement_account?.bank,
            name: 'bank',
        },
        {
            label: 'Account Number',
            placeholder: '3414 2342 3422 2312',
            value: storeDetail.settlement_account?.account,
            name: 'account',
        },
        {
            label: 'Account Name',
            placeholder: '3134 Design and Styles',
            value: storeDetail.settlement_account?.account_name,
            name: 'account_name',
        },
    ];

    return (
        <DashboardSection>
            <Box
                maxWidth={{
                    base: '60%',
                    sm: '80%',
                    md: '60%',
                    lg: '60%',
                }}
                display="flex"
                flexDirection="column"
                gap="2rem">
                <Text
                    width="100%"
                    fontWeight="400"
                    fontSize={{
                        base: '1rem',
                        sm: '1rem',
                        md: '1.2rem',
                        lg: '1.225rem',
                        xl: '1.25rem',
                    }}>
                    Please fill in the form
                </Text>
                <FormControl>
                    {formArr.map((item, index) => {
                        return (
                            <>
                                <FormControl marginBottom="1.4rem" fontSize="sm" fontWeight="300">
                                    <FormLabel
                                        color="#344054"
                                        fontSize={{
                                            base: '.8rem',
                                            sm: '1rem',
                                            md: '1rem',
                                            lg: '1rem',
                                        }}>
                                        {item.label}
                                    </FormLabel>
                                    {item.label === 'Store Link' ? (
                                        <InputGroup
                                            color="#667085"
                                            display="flex"
                                            flexDirection={{
                                                base: 'column',
                                                sm: 'column',
                                                md: 'row',
                                                lg: 'row',
                                            }}>
                                            <InputLeftAddon
                                                children={item.addon}
                                                background="transparent"
                                                opacity="0.7"
                                                fontSize={{
                                                    base: '',
                                                    xs: '.6rem',
                                                    sm: '.9rem',
                                                    md: '1rem',
                                                    lg: '1rem',
                                                }}
                                            />
                                            <Input
                                                type="text"
                                                placeholder={item.placeholder}
                                                value={item.value}
                                                name={item.name}
                                                onChange={(e) => handleChange(e)}
                                                fontSize={{
                                                    base: '',
                                                    xs: '.6rem',
                                                    sm: '.9rem',
                                                    md: '1rem',
                                                    lg: '1rem',
                                                }}
                                            />
                                        </InputGroup>
                                    ) : item.label === 'Social Handles' ? (
                                        [0, 1, 2].map((number, numberIndex) => {
                                            return (
                                                <InputGroup
                                                    color="#667085"
                                                    marginBottom=".8rem"
                                                    display="flex"
                                                    flexDirection={{
                                                        base: 'column',
                                                        sm: 'column',
                                                        md: 'row',
                                                        lg: 'row',
                                                    }}>
                                                    <InputLeftAddon
                                                        children={item.addon[number]}
                                                        background="transparent"
                                                        fontSize={{
                                                            base: '',
                                                            xs: '.6rem',
                                                            sm: '.9rem',
                                                            md: '1rem',
                                                            lg: '',
                                                        }}
                                                    />
                                                    <Input
                                                        type="text"
                                                        placeholder={item.placeholder[number]}
                                                        value={item.value[number]}
                                                        name={item.name[number]}
                                                        onChange={(e) => handleChange(e)}
                                                        fontSize={{
                                                            base: '',
                                                            sm: '.9rem',
                                                            md: '1rem',
                                                            lg: '1rem',
                                                        }}
                                                    />
                                                </InputGroup>
                                            );
                                        })
                                    ) : item.label === 'Settlement Account' ? (
                                        <Select
                                            name={item.name}
                                            placeholder={item.placeholder[0]}
                                            opacity="0.7"
                                            onChange={(e) => handleChange(e)}>
                                            {[1, 2, 3].map((option) => {
                                                return (
                                                    <option
                                                        value={item.placeholder[option]}
                                                        opacity="0.6">
                                                        {item.placeholder[option]}
                                                    </option>
                                                );
                                            })}
                                        </Select>
                                    ) : item.label === 'Upload Logo' ? (
                                        <>
                                            <Input
                                                type="file"
                                                name={'Image name'}
                                                ref={fileInput}
                                                hidden
                                                multiple
                                                accept="image/jpeg,image/png,image/webp"
                                                onChange={(e) =>
                                                    setSelectedImage(e.target.files[0])
                                                }
                                            />
                                            <Text onDragEnter={handleDrag}>
                                                <Box
                                                    display="flex"
                                                    justifyContent={{
                                                        base: '',
                                                        sm: '',
                                                        md: 'center',
                                                        lg: 'center',
                                                    }}
                                                    width="100%"
                                                    gap="1rem"
                                                    border="2px solid"
                                                    borderColor="inherit"
                                                    borderRadius=".4rem"
                                                    padding={{
                                                        base: '.2rem',
                                                        sm: '.2rem',
                                                        md: '.4rem',
                                                        lg: '.4rem',
                                                    }}
                                                    fontSize={{
                                                        base: '0.5rem',
                                                        sm: '0.7rem',
                                                        md: '0.9rem',
                                                        lg: '0.9rem',
                                                        xl: '1rem',
                                                    }}>
                                                    <Image
                                                        src={uploadCloud}
                                                        alt=""
                                                        onClick={() => fileInput.current.click()}
                                                    />
                                                    <Text
                                                        color="#0765FF"
                                                        display="inline"
                                                        onClick={() => fileInput.current.click()}>
                                                        Click here to upload files{' '}
                                                        <Text color="#667085" display="inline">
                                                            or drag and drop
                                                        </Text>
                                                    </Text>
                                                </Box>
                                            </Text>
                                        </>
                                    ) : (
                                        <Input
                                            type="email"
                                            placeholder={item.placeholder}
                                            value={item.value}
                                            name={item.name}
                                            onChange={(e) => handleChange(e)}
                                        />
                                    )}
                                </FormControl>
                            </>
                        );
                    })}
                    <Button
                        maxWidth="8rem"
                        fontSize="xs"
                        background="#1b3b5f"
                        color="#ffffff"
                        onClick={handleSubmit}>
                        Create a store
                    </Button>
                </FormControl>
            </Box>
            <StoreCreationSuccessModal
                isOpen={isSuccessModalOpen}
                onClose={closeSuccessModal}
                storeName={storeDetail.name}
                storeLink={`${config.baseUrl}/store/${storeDetail?.link}`}
                proceedClicked={() => navigate('/dashboard/store/products/create-product')}
            />
        </DashboardSection>
    );
}
